/**
 * GaleriaImagenes — Sección de galería reutilizable para entidades con fotos
 * (embarcaciones, paquetes, destinos, etc).
 *
 * Endpoints esperados (parametrizables via prop `endpoint`):
 *   GET    {endpoint}/{id}/imagenes
 *   POST   {endpoint}/{id}/imagenes  (multipart: archivo)
 *   PATCH  {endpoint}/imagenes/{imgId}              (body: { descripcion })
 *   PATCH  {endpoint}/imagenes/{imgId}/principal    (marcar como principal)
 *   DELETE {endpoint}/imagenes/{imgId}
 *
 * Props:
 *   entidadId       int — id de la entidad (embarcacion / paquete)
 *   endpoint        string — base, ej. "/turismo/embarcaciones" o "/turismo/paquetes"
 *   color           string — acento (default '#0d9488')
 *   maxImagesShown  number — opcional, sin paginación si rebasa
 *   onCount         (count) => void — informa cuántas hay
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, IconButton, Button, Chip, CircularProgress, Tooltip, TextField,
} from '@mui/material';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ConfirmDialog from './ConfirmDialog';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function getAuthHeaders(json = true) {
  const t = localStorage.getItem('token');
  const h = { ...(t ? { Authorization: `Bearer ${t}` } : {}) };
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

export default function GaleriaImagenes({
  entidadId,
  endpoint,                // ej "/turismo/embarcaciones"
  color = '#0d9488',
  onCount,
}) {
  const [imagenes, setImagenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingDesc, setEditingDesc] = useState(null);  // {id, value}

  const cargar = useCallback(async () => {
    if (!entidadId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}${endpoint}/${entidadId}/imagenes`, { headers: getAuthHeaders() });
      const data = await r.json();
      setImagenes(Array.isArray(data) ? data : []);
      onCount?.(Array.isArray(data) ? data.length : 0);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [endpoint, entidadId, onCount]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('archivo', file);
        await fetch(`${API_BASE}${endpoint}/${entidadId}/imagenes`, {
          method: 'POST', headers: getAuthHeaders(false), body: fd,
        });
      }
      await cargar();
    } catch (err) { alert('Error subiendo imagen: ' + err.message); }
    setUploading(false);
    e.target.value = '';
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await fetch(`${API_BASE}${endpoint}/imagenes/${deleteTarget.id}`, { method: 'DELETE', headers: getAuthHeaders() });
      setDeleteTarget(null);
      cargar();
    } catch (e) { alert('Error: ' + e.message); }
  };

  const handlePrincipal = async (id) => {
    await fetch(`${API_BASE}${endpoint}/imagenes/${id}/principal`, { method: 'PATCH', headers: getAuthHeaders() });
    cargar();
  };

  const handleSaveDesc = async () => {
    if (!editingDesc) return;
    await fetch(`${API_BASE}${endpoint}/imagenes/${editingDesc.id}`, {
      method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify({ descripcion: editingDesc.value }),
    });
    setEditingDesc(null);
    cargar();
  };

  if (!entidadId) {
    return (
      <Box sx={{ py: 3, textAlign: 'center', color: '#94a3b8' }}>
        <PhotoLibraryIcon sx={{ fontSize: 36, mb: 0.5 }} />
        <Typography sx={{ fontSize: 12 }}>Guarda primero para poder subir fotos</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Button component="label" variant="outlined" size="small" startIcon={<CloudUploadIcon />}
        disabled={uploading} fullWidth
        sx={{ mb: 2, textTransform: 'none', borderColor: color, color }}>
        {uploading ? 'Subiendo...' : 'Subir imágenes (JPG/PNG/WebP, máx 5MB c/u)'}
        <input hidden accept="image/jpeg,image/png,image/webp" type="file" multiple onChange={handleUpload} />
      </Button>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={28} sx={{ color }} /></Box>
      ) : imagenes.length === 0 ? (
        <Box sx={{ py: 3, textAlign: 'center', color: '#94a3b8' }}>
          <PhotoLibraryIcon sx={{ fontSize: 36, mb: 0.5 }} />
          <Typography sx={{ fontSize: 12 }}>Aún no hay imágenes. Sube la primera.</Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 1 }}>
          {imagenes.map(img => (
            <Box key={img.id} sx={{
              position: 'relative', borderRadius: 2, overflow: 'hidden', aspectRatio: '1/1',
              border: img.es_principal ? `3px solid ${color}` : '1px solid #e5e7eb',
              boxShadow: img.es_principal ? `0 0 12px ${color}40` : '0 1px 4px rgba(0,0,0,0.06)',
            }}>
              <img src={`${API_BASE}${img.imagen_url}`} alt={img.descripcion || ''}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />

              {img.es_principal && (
                <Chip icon={<StarIcon sx={{ fontSize: 12, color: '#fff !important' }} />} label="Principal" size="small"
                  sx={{ position: 'absolute', top: 4, left: 4, height: 20, fontSize: 10, fontWeight: 700,
                        bgcolor: color, color: '#fff' }} />
              )}
              {img.descripcion && (
                <Chip label={img.descripcion} size="small"
                  sx={{ position: 'absolute', bottom: 4, left: 4, maxWidth: 'calc(100% - 8px)',
                        height: 20, fontSize: 10, fontWeight: 600,
                        bgcolor: 'rgba(15,23,42,0.85)', color: '#fff',
                        '& .MuiChip-label': { px: 0.75 } }} />
              )}

              <Box sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Tooltip title="Etiqueta">
                  <IconButton size="small" onClick={() => setEditingDesc({ id: img.id, value: img.descripcion || '' })}
                    sx={{ bgcolor: 'rgba(255,255,255,0.95)', width: 24, height: 24, '&:hover': { bgcolor: '#fff' } }}>
                    <EditIcon sx={{ fontSize: 14, color: '#0ea5e9' }} />
                  </IconButton>
                </Tooltip>
                {!img.es_principal && (
                  <Tooltip title="Marcar principal">
                    <IconButton size="small" onClick={() => handlePrincipal(img.id)}
                      sx={{ bgcolor: 'rgba(255,255,255,0.95)', width: 24, height: 24, '&:hover': { bgcolor: '#fff' } }}>
                      <StarBorderIcon sx={{ fontSize: 14, color: '#f59e0b' }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Eliminar">
                  <IconButton size="small" onClick={() => setDeleteTarget(img)}
                    sx={{ bgcolor: 'rgba(255,255,255,0.95)', width: 24, height: 24, '&:hover': { bgcolor: '#fee2e2' } }}>
                    <DeleteOutlineIcon sx={{ fontSize: 14, color: '#dc2626' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Edit etiqueta */}
      {editingDesc && (
        <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField size="small" fullWidth label="Etiqueta"
            value={editingDesc.value}
            onChange={e => setEditingDesc({ ...editingDesc, value: e.target.value })}
            placeholder="Ej: cubierta, interior, exterior..."
            autoFocus />
          <Button size="small" variant="contained" disableElevation onClick={handleSaveDesc}
            sx={{ textTransform: 'none', bgcolor: color }}>Guardar</Button>
          <Button size="small" onClick={() => setEditingDesc(null)} sx={{ textTransform: 'none' }}>Cancelar</Button>
        </Box>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="¿Eliminar esta imagen?"
        message="La imagen se eliminará permanentemente del servidor."
        confirmText="Eliminar"
      />
    </Box>
  );
}
